import { supabase } from '../lib/supabase'
import { FIELD_COMPLETE_STATUSES } from '../lib/jobStatusWorkflow'
import {
  buildDivisionPostgrestFilter,
  type OperationalDivision,
} from '../lib/operationalDivisions'

export interface CalendarMonthStats {
  total: number
  scheduled: number
  inProgress: number
  completed: number
}

export const EMPTY_CALENDAR_MONTH_STATS: CalendarMonthStats = {
  total: 0,
  scheduled: 0,
  inProgress: 0,
  completed: 0,
}

export interface FetchCalendarMonthStatsOptions {
  rangeStart: string
  rangeEnd: string
  division: OperationalDivision | null
  statusFilter: string
  visibilityOrFilter?: string | null
}

async function countCalendarJobs(apply: (q: any) => any): Promise<number> {
  let q = supabase.from('jobs').select('id', { count: 'exact', head: true })
  q = apply(q)
  const { count, error } = await q
  if (error) throw error
  return count || 0
}

function applyDivisionFilter(q: any, division: OperationalDivision | null) {
  if (!division) return q
  return q.or(buildDivisionPostgrestFilter(division))
}

function applyDateRange(q: any, rangeStart: string, rangeEnd: string) {
  return q.gte('scheduled_date', rangeStart).lte('scheduled_date', rangeEnd)
}

function applyStatusFilter(q: any, statusFilter: string) {
  if (statusFilter === 'active') {
    return q.in('status', ['draft', 'scheduled', 'in_progress', 'pending_review'])
  }
  if (statusFilter !== 'all') {
    return q.eq('status', statusFilter)
  }
  return q
}

function buildScopedQuery(opts: FetchCalendarMonthStatsOptions) {
  return (q: any) => {
    let next = applyDateRange(q, opts.rangeStart, opts.rangeEnd)
    next = applyStatusFilter(next, opts.statusFilter)
    next = applyDivisionFilter(next, opts.division)
    if (opts.visibilityOrFilter) {
      next = next.or(opts.visibilityOrFilter)
    }
    return next
  }
}

export async function fetchCalendarMonthStats(
  opts: FetchCalendarMonthStatsOptions
): Promise<CalendarMonthStats> {
  const scoped = buildScopedQuery(opts)

  const [total, scheduled, inProgress, completed] = await Promise.all([
    countCalendarJobs(scoped),
    countCalendarJobs((q) => scoped(q).eq('status', 'scheduled')),
    countCalendarJobs((q) => scoped(q).eq('status', 'in_progress')),
    countCalendarJobs((q) => scoped(q).in('status', [...FIELD_COMPLETE_STATUSES])),
  ])

  return { total, scheduled, inProgress, completed }
}

const PAGE_SIZE = 1000

export async function fetchAllCalendarJobs<T>(build: () => any): Promise<T[]> {
  const rows: T[] = []
  let from = 0
  for (;;) {
    const { data, error } = await build().range(from, from + PAGE_SIZE - 1)
    if (error) throw error
    const page = (data || []) as T[]
    rows.push(...page)
    if (page.length < PAGE_SIZE) break
    from += PAGE_SIZE
  }
  return rows
}
